/**
 * The Take Ur Seat family of restaurants — +Bake Cafe's sister brands.
 *
 * Source of truth for the "family" section on /about. Every field here was
 * verified against the group's own Toast POS (addresses, geo, first business
 * date) plus each brand's own public website. Nothing is paraphrased from a
 * third-party aggregator.
 *
 * DELIBERATE OMISSION — no street address, phone, or opening hours.
 * Publishing a sister location's full NAP on plusbake.com creates a local
 * citation that this site would then have to keep in sync forever; the moment
 * it drifts it becomes an *inconsistent* citation that actively hurts the
 * sister's local ranking. Each block therefore carries city + concept + a link
 * to that brand's own canonical page, which is where its NAP and schema live.
 * If the group later wants full NAP here, add the fields to this type — the
 * page reads everything from this array.
 */
export type TusFamilyBrand = {
  /** Stable key — also used as the section anchor id. */
  id: string
  /** Brand name exactly as the business writes it on its own signage/site. */
  name: string
  city: string
  region: string
  /** The brand's own tagline, as published by that brand. */
  tagline: string
  /** One-line format descriptor shown under the heading. */
  format: string
  /** ≤70 words. Describes the *relationship* to +Bake, not "best brunch in X". */
  blurb: string
  /** Canonical page for this brand — where its own NAP + schema live. */
  url: string
  /** Anchor text for the outbound link. Always "Brand + in + City". */
  linkLabel: string
  exterior: { src: string; alt: string }
  interior: { src: string; alt: string }
}

const IMG = "/images/plusbake/family"

export const TUS_FAMILY: TusFamilyBrand[] = [
  {
    id: "take-ur-seat-chino-hills",
    name: "Take Ur Seat",
    city: "Chino Hills",
    region: "CA",
    // Punctuated with periods on the storefront sign and on takeurseat.com —
    // not the comma form ("You, Me, Brunch") used in some internal docs.
    tagline: "You. Me. Brunch.",
    format: "Full-service brunch restaurant — the original",
    blurb:
      "The restaurant that started everything. Chino Hills is where the group learned to put Filipino and Southeast Asian flavors next to Western brunch technique, and where the espresso bar that became Local Coffee Roasters first opened. +Bake grew directly out of this kitchen.",
    url: "https://takeurseat.com/takeurseatchinohills",
    linkLabel: "Take Ur Seat in Chino Hills",
    exterior: {
      src: `${IMG}/tus-chino-hills-exterior.webp`,
      alt: "Exterior of Take Ur Seat in Chino Hills, with its red awning and 'You, Me, Brunch' storefront sign.",
    },
    interior: {
      src: `${IMG}/tus-chino-hills-interior.webp`,
      alt: "Dining room inside Take Ur Seat Chino Hills — wood tables, white metal chairs, and a neon sign reading 'Together is a beautiful place to be'.",
    },
  },
  {
    id: "take-ur-seat-too-ontario",
    name: "Take Ur Seat Too",
    city: "Ontario",
    region: "CA",
    // Exactly as the Ontario storefront sign reads — no trailing period.
    tagline: "You. Me. Brunch. Again",
    format: "Full-service brunch restaurant — the second location",
    blurb:
      "Opened in Ontario in 2022, the same year as +Bake. Same kitchen playbook as Chino Hills, with a brighter room built around a plant-filled photo corner. It is the closest full-service sibling to the bakery — about fifteen minutes down the 15 from Base Line Rd.",
    url: "https://takeurseat.com/takeurseatontario",
    linkLabel: "Take Ur Seat in Ontario",
    exterior: {
      src: `${IMG}/tus-ontario-exterior.webp`,
      alt: "Exterior of Take Ur Seat Too in Ontario, California, with the 'You. Me. Brunch. Again.' sign above the entrance.",
    },
    interior: {
      src: `${IMG}/tus-ontario-interior.webp`,
      alt: "Interior corner at Take Ur Seat Too Ontario — a hanging rattan chair, tropical plants, and a pink neon script sign.",
    },
  },
  {
    // Opened in 2024 as Gud2Go; renamed Take Ur Seat Cafe in 2026. gud2go.com
    // now redirects to takeurseatcafe.com, and Gud2Go lives on as the café's
    // grab-and-go counter line — the tagline below is the café's own.
    id: "take-ur-seat-cafe-pomona",
    name: "Take Ur Seat Cafe",
    city: "Pomona",
    region: "CA",
    tagline: "Take Ur Seat. Or Gud2Go.",
    format: "Brunch & coffee café, with Gud2Go as its grab-and-go counter",
    blurb:
      "Opened in 2024 as Gud2Go inside The Union on Garey, a restored 1922 brick landmark in downtown Pomona near Cal Poly Pomona, and renamed Take Ur Seat Cafe in 2026. Same room, same team, same menu, with Gud2Go still running the grab-and-go counter. It shares +Bake's pastry and drink thinking.",
    url: "https://www.takeurseatcafe.com",
    linkLabel: "Take Ur Seat Cafe in Pomona",
    exterior: {
      src: `${IMG}/gud2go-pomona-exterior.webp`,
      alt: "Exterior of The Union on Garey in downtown Pomona, the restored brick building that houses Take Ur Seat Cafe.",
    },
    interior: {
      src: `${IMG}/gud2go-pomona-interior.webp`,
      alt: "Order counter inside Take Ur Seat Cafe in Pomona — pastry case, digital menu boards, and exposed brick walls.",
    },
  },
  {
    id: "take-ur-seat-lite-redlands",
    name: "Take Ur Seat Lite",
    city: "Redlands",
    region: "CA",
    tagline: "Take Ur Seat. Stay a While.",
    format: "All-day quick-service counter inside Redlands Public Market",
    blurb:
      "The family's furthest-east outpost, in the 1890 packing house that is now Redlands Public Market. Counter service from morning to night — rice bowls, burritos, and matcha — which makes it the one sibling you can still walk into after the bakery has closed for the day.",
    url: "https://tuslite.com",
    linkLabel: "Take Ur Seat Lite in Redlands",
    exterior: {
      src: `${IMG}/tus-lite-redlands-exterior.webp`,
      alt: "The illuminated Redlands Public Market sign above the entrance to the downtown food hall where Take Ur Seat Lite serves.",
    },
    interior: {
      src: `${IMG}/tus-lite-redlands-interior.webp`,
      alt: "The Take Ur Seat Lite counter inside Redlands Public Market, with guests ordering under the hexagon logo sign.",
    },
  },
]

/** The group's parent site — used for the Organization @id and intro link. */
export const TUS_GROUP_URL = "https://www.takeurseat.com"
